// REST client. Every response body is validated with the shared zod schemas;
// nothing from the network is trusted as-is.
import type { z } from "zod";
import {
  ActivityConfigResponseSchema,
  ActivityLobbyResponseSchema,
  ActivityLoginResponseSchema,
  type ActivityLoginResponse,
  WsTicketResponseSchema,
  ApiErrorSchema,
  CreateLobbyBodySchema,
  CreateLobbyResponseSchema,
  WeeklyClaimResponseSchema,
  type WeeklyClaimResponse,
  JoinLobbyResponseSchema,
  LobbyListResponseSchema,
  MeSchema,
  PASSWORD_MAX,
  PASSWORD_MIN,
  PasswordSchema,
  ProvidersResponseSchema,
  RoomCodeResponseSchema,
  SetAdminResponseSchema,
  AdminPlayerSchema,
  type AdminPlayer,
  AdminOrbsResponseSchema,
  type AdminGiveOrbsBody,
  type AdminOrbsResponse,
  FeedbackListResponseSchema,
  AdminStatsResponseSchema,
  type AdminStatsResponse,
  AdminBroadcastResponseSchema,
  AdminCloseLobbyResponseSchema,
  type AdminBroadcastBody,
  type AdminBroadcastResponse,
  NoticesResponseSchema,
  type NoticesResponse,
  type FeedbackKind,
  type FeedbackListResponse,
  USERNAME_MAX,
  UsernameSchema,
  type CreateLobbyBody,
  type LobbySummary,
  type Me,
  type OAuthProvider,
  SlotSpinResponseSchema,
  type SlotSpinResponse,
  ArcadeResponseSchema,
  type ArcadeBody,
  type ArcadeResponse,
  OrbResultSchema,
  type OrbResult,
  type OrbTier,
} from "@wp/shared";
import { err, ok, type Result } from "../lib/result";

export interface ApiFailure {
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;
  /** Server error code (ApiError.error) or a client-side code. */
  code: string;
  /** Friendly text that can be shown to the user. */
  message: string;
}

export type ApiResult<T> = Result<T, ApiFailure>;

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * (server clock - client clock) in ms from an HTTP Date header, or null when
 * the header is missing or unreadable. The header has whole seconds (rounded
 * down), so half a second is added; the request is assumed to have reached the
 * server halfway between sending and receiving.
 */
export function offsetFromDateHeader(header: string | null, sentAt: number, receivedAt: number): number | null {
  if (!header) return null;
  const serverMs = Date.parse(header);
  if (!Number.isFinite(serverMs)) return null;
  return Math.round(serverMs + 500 - (sentAt + receivedAt) / 2);
}

const NETWORK_FAILURE: ApiFailure = {
  status: 0,
  code: "network",
  message: "Can't reach the server. Check your connection and try again.",
};

function badResponse(status: number): ApiFailure {
  return { status, code: "bad_response", message: "The server sent an unexpected response. Please try again." };
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return (await res.json()) as unknown;
  } catch {
    return undefined;
  }
}

export interface Api {
  /**
   * Estimated (server clock - this device's clock) in ms, learned from the
   * Date header of API responses; 0 until known. Server timestamps such as
   * Me.weekly.nextDayAt are compared with Date.now() + serverOffset().
   */
  serverOffset(): number;
  /**
   * Called when a request that needs a login gets 401: the session expired or
   * was logged out (e.g. in another tab). Not for /api/me (App handles that
   * one: no session yet is normal there) or the login, register and logout
   * calls. Pass null to remove it.
   */
  onSessionEnded(handler: ((failure: ApiFailure) => void) | null): void;
  /**
   * Discord Activity: send this session token as `Authorization: Bearer` on
   * every request instead of relying on the cookie (kept in memory only).
   * null goes back to cookies.
   */
  setSessionToken(token: string | null): void;
  /** True while a bearer session token is set (inside the Discord Activity). */
  usesBearer(): boolean;
  /** Discord Activity: the Discord application id to authorize with. */
  activityConfig(): Promise<ApiResult<{ clientId: string }>>;
  /** Discord Activity: trade the SDK's authorize() code for a session (sets nothing by itself). */
  activityLogin(code: string, instanceId?: string): Promise<ApiResult<ActivityLoginResponse>>;
  /** Discord Activity: join or open the table of this Activity instance. */
  activityLobby(instanceId: string, pass: string): Promise<ApiResult<{ id: string }>>;
  /** One-time ticket to open a lobby WebSocket without a cookie (bearer sessions). */
  wsTicket(): Promise<ApiResult<{ ticket: string }>>;
  me(): Promise<ApiResult<Me>>;
  providers(): Promise<ApiResult<{ discord: boolean; google: boolean }>>;
  login(username: string, password: string): Promise<ApiResult<Me>>;
  register(username: string, password: string, email?: string): Promise<ApiResult<Me>>;
  logout(): Promise<ApiResult<void>>;
  /** Claim today's weekly login reward (coins or a free orb pull). */
  claimWeekly(): Promise<ApiResult<WeeklyClaimResponse>>;
  listLobbies(): Promise<ApiResult<LobbySummary[]>>;
  createLobby(body: CreateLobbyBody): Promise<ApiResult<{ id: string; code: string }>>;
  joinLobby(id: string, password?: string): Promise<ApiResult<void>>;
  /** The open lobby behind a room code. */
  findRoom(code: string): Promise<ApiResult<{ id: string; isPrivate: boolean; name: string }>>;
  updateProfile(patch: { displayName?: string; email?: string | null }): Promise<ApiResult<Me>>;
  changeUsername(username: string): Promise<ApiResult<Me>>;
  changePassword(currentPassword: string, newPassword: string): Promise<ApiResult<void>>;
  deleteAccount(confirm: { password?: string; confirmUsername?: string }): Promise<ApiResult<void>>;
  equip(category: string, itemId: string | null): Promise<ApiResult<Me>>;
  /** Pull the slot machine's lever (the server spins and pays). */
  spinSlots(bet: number): Promise<ApiResult<SlotSpinResponse>>;
  /** One move on an arcade cabinet (Minesweeper, Stacker): the server plays it and pays any coins. */
  playArcade(body: ArcadeBody): Promise<ApiResult<ArcadeResponse>>;
  /** Buy a shop item with coins. */
  buyItem(itemId: string): Promise<ApiResult<Me>>;
  /** Open an orb (the server rolls the item); `free` spends a free pull instead of coins. */
  openOrb(tier: OrbTier, free?: boolean): Promise<ApiResult<OrbResult>>;
  setAdmin(username: string, isAdmin: boolean): Promise<ApiResult<{ username: string; isAdmin: boolean }>>;
  /** Admin: set a player's coins exactly. */
  adminSetCoins(username: string, coins: number): Promise<ApiResult<AdminPlayer>>;
  /** Admin: give a player free orb pulls of one tier. */
  adminGiveOrbs(body: AdminGiveOrbsBody): Promise<ApiResult<AdminOrbsResponse>>;
  /** Admin: give (owned) or take away items; "all" = every item. */
  adminSetUnlocks(username: string, itemIds: "all" | string[], owned: boolean): Promise<ApiResult<AdminPlayer>>;
  /** Send a bug report or suggestion (Settings > Support). */
  sendFeedback(kind: FeedbackKind, message: string): Promise<ApiResult<void>>;
  /** Admin: stored feedback, newest first; `before` = nextBefore of the previous page. */
  adminFeedback(before?: number | null): Promise<ApiResult<FeedbackListResponse>>;
  adminStats(asOf: number): Promise<ApiResult<AdminStatsResponse>>;
  /** Messages from the team not acknowledged yet, oldest first. */
  notices(): Promise<ApiResult<NoticesResponse>>;
  /** This message was read: it is not shown again (on any device). */
  ackNotice(id: number): Promise<ApiResult<void>>;
  /** Admin: a message window for everyone online, or for named players. */
  adminBroadcast(body: AdminBroadcastBody): Promise<ApiResult<AdminBroadcastResponse>>;
  /** Admin: close any lobby, like its host would (the hand is settled or refunded; everyone is sent back to the menu). */
  adminCloseLobby(lobbyId: string): Promise<ApiResult<void>>;
}

export function createApi(
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
  clientNow: () => number = Date.now,
): Api {
  let offset = 0;
  let sessionEnded: ((failure: ApiFailure) => void) | null = null;
  /** Bearer session token (Discord Activity only). Never stored beyond this page's memory. */
  let bearer: string | null = null;

  /** A request that needs a login: a 401 means the session is gone. */
  async function authed<S extends z.ZodType>(
    method: "GET" | "POST" | "PATCH",
    path: string,
    schema: S | null,
    body?: unknown,
  ): Promise<ApiResult<z.infer<S>>> {
    const res = await request(method, path, schema, body);
    if (!res.ok && res.error.status === 401) sessionEnded?.(res.error);
    return res;
  }

  /**
   * One request. POSTs always carry a JSON body with content-type
   * application/json (the server rejects anything else).
   */
  async function request<S extends z.ZodType>(
    method: "GET" | "POST" | "PATCH",
    path: string,
    schema: S | null,
    body?: unknown,
  ): Promise<ApiResult<z.infer<S>>> {
    const headers: Record<string, string> = { accept: "application/json" };
    // Weekly reward days turn at the player's own midnight.
    const tz = localTimeZone();
    if (tz) headers["x-time-zone"] = tz;
    if (bearer !== null) headers.authorization = `Bearer ${bearer}`;
    // With a bearer token, cookies are left out entirely: one way to sign in at a time.
    const init: RequestInit = { method, credentials: bearer !== null ? "omit" : "same-origin", headers };
    if (method !== "GET") {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(body ?? {});
    }
    let res: Response;
    const sentAt = clientNow();
    try {
      res = await fetchImpl(path, init);
    } catch {
      return err(NETWORK_FAILURE);
    }
    offset = offsetFromDateHeader(res.headers.get("date"), sentAt, clientNow()) ?? offset;
    if (!res.ok) {
      const parsed = ApiErrorSchema.safeParse(await readJson(res));
      if (parsed.success) return err({ status: res.status, code: parsed.data.error, message: parsed.data.message });
      return err({ status: res.status, code: "http_error", message: `Request failed (${res.status}).` });
    }
    if (schema === null) return ok(undefined as z.infer<S>);
    const parsed = schema.safeParse(await readJson(res));
    return parsed.success ? ok(parsed.data) : err(badResponse(res.status));
  }

  return {
    serverOffset: () => offset,
    onSessionEnded: (handler) => {
      sessionEnded = handler;
    },
    setSessionToken: (token) => {
      bearer = token;
    },
    usesBearer: () => bearer !== null,
    activityConfig: () => request("GET", "/api/auth/discord/activity/config", ActivityConfigResponseSchema),
    activityLogin: (code, instanceId) =>
      request("POST", "/api/auth/discord/activity", ActivityLoginResponseSchema, instanceId ? { code, instanceId } : { code }),
    activityLobby: (instanceId, pass) => authed("POST", "/api/lobbies/activity", ActivityLobbyResponseSchema, { instanceId, pass }),
    // Not authed(): a 401 here reaches the lobby socket, which reports "signed out" itself.
    wsTicket: () => request("POST", "/api/me/ws-ticket", WsTicketResponseSchema),
    me: () => request("GET", "/api/me", MeSchema),
    providers: () => request("GET", "/api/auth/providers", ProvidersResponseSchema),
    login: (username, password) => request("POST", "/api/auth/login", MeSchema, { username, password }),
    register: (username, password, email) =>
      request("POST", "/api/auth/register", MeSchema, email ? { username, password, email } : { username, password }),
    logout: async () => {
      const res = await request("POST", "/api/auth/logout", null);
      if (res.ok) bearer = null;
      return res.ok ? ok(undefined) : res;
    },
    claimWeekly: () => authed("POST", "/api/me/rewards/claim", WeeklyClaimResponseSchema),
    listLobbies: async () => {
      const res = await authed("GET", "/api/lobbies", LobbyListResponseSchema);
      return res.ok ? ok(res.value.lobbies) : res;
    },
    createLobby: async (body) => {
      const valid = CreateLobbyBodySchema.safeParse(body);
      if (!valid.success) {
        return err({ status: 0, code: "invalid", message: valid.error.issues[0]?.message ?? "Invalid lobby." });
      }
      return authed("POST", "/api/lobbies", CreateLobbyResponseSchema, valid.data);
    },
    joinLobby: async (id, password) => {
      const res = await authed(
        "POST",
        `/api/lobbies/${encodeURIComponent(id)}/join`,
        JoinLobbyResponseSchema,
        password === undefined ? {} : { password },
      );
      return res.ok ? ok(undefined) : res;
    },
    findRoom: (code) =>
      authed("GET", `/api/lobbies/code/${encodeURIComponent(code.trim().toUpperCase())}`, RoomCodeResponseSchema),
    updateProfile: (patch) => authed("PATCH", "/api/me/profile", MeSchema, patch),
    changeUsername: (username) => authed("POST", "/api/me/username", MeSchema, { username }),
    changePassword: async (currentPassword, newPassword) => {
      const res = await authed("POST", "/api/me/password", null, { currentPassword, newPassword });
      return res.ok ? ok(undefined) : res;
    },
    deleteAccount: async (confirm) => {
      const res = await authed("POST", "/api/me/delete", null, confirm);
      return res.ok ? ok(undefined) : res;
    },
    equip: (category, itemId) => authed("POST", "/api/me/equip", MeSchema, { category, itemId }),
    spinSlots: (bet) => authed("POST", "/api/me/slots", SlotSpinResponseSchema, { bet }),
    playArcade: (body) => authed("POST", "/api/me/arcade", ArcadeResponseSchema, body),
    buyItem: (itemId) => authed("POST", "/api/me/shop/buy", MeSchema, { itemId }),
    openOrb: (tier, free) => authed("POST", "/api/me/orbs/open", OrbResultSchema, free ? { tier, free: true } : { tier }),
    setAdmin: (username, isAdmin) => authed("POST", "/api/admin/admins", SetAdminResponseSchema, { username, isAdmin }),
    adminSetCoins: (username, coins) => authed("POST", "/api/admin/coins", AdminPlayerSchema, { username, coins }),
    adminGiveOrbs: (body) => authed("POST", "/api/admin/orbs", AdminOrbsResponseSchema, body),
    adminSetUnlocks: (username, itemIds, owned) => authed("POST", "/api/admin/unlocks", AdminPlayerSchema, { username, itemIds, owned }),
    sendFeedback: async (kind, message) => {
      const res = await authed("POST", "/api/me/feedback", null, { kind, message });
      return res.ok ? ok(undefined) : res;
    },
    notices: () => authed("GET", "/api/me/notices", NoticesResponseSchema),
    ackNotice: async (id) => {
      const res = await authed("POST", "/api/me/notices/ack", null, { id });
      return res.ok ? ok(undefined) : res;
    },
    adminBroadcast: (body) => authed("POST", "/api/admin/broadcast", AdminBroadcastResponseSchema, body),
    adminCloseLobby: async (lobbyId) => {
      const res = await authed("POST", `/api/admin/lobbies/${encodeURIComponent(lobbyId)}/close`, AdminCloseLobbyResponseSchema);
      return res.ok ? ok(undefined) : res;
    },
    adminStats: (asOf) => authed("GET", `/api/admin/stats?asOf=${asOf}`, AdminStatsResponseSchema),
    adminFeedback: (before) =>
      authed("GET", before == null ? "/api/admin/feedback" : `/api/admin/feedback?before=${before}`, FeedbackListResponseSchema),
  };
}

export const api: Api = createApi();

/** Full-page navigation to start an OAuth login. */
export function oauthStartPath(provider: OAuthProvider): string {
  return `/api/auth/oauth/${provider}/start`;
}

/** Client-side check before POST /api/auth/register. Returns a friendly message or null. */
export function registerProblem(username: string, password: string): string | null {
  const u = UsernameSchema.safeParse(username);
  if (!u.success) {
    if (username.length === 0) return "Pick a username.";
    if (username.length > USERNAME_MAX) return `Usernames can be at most ${USERNAME_MAX} characters.`;
    return "Usernames use letters, numbers, spaces, _ - . (no space at the start or end).";
  }
  if (!PasswordSchema.safeParse(password).success) return `Passwords must be ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`;
  return null;
}

/** Client-side check before POST /api/auth/login. */
export function loginProblem(username: string, password: string): string | null {
  if (username.length === 0 || password.length === 0) return "Enter your username and password.";
  return null;
}

/**
 * Bearer sessions (Discord Activity): a WebSocket cannot carry an
 * Authorization header, so each connection attempt first gets a one-time
 * ticket (valid for a minute) and passes that in the URL. The session token
 * itself never goes in a URL. "signedOut" when the session is gone, null on
 * any other failure (the socket retries).
 */
export async function withSocketTicket(url: string, client: Api = api): Promise<string | null | "signedOut"> {
  const res = await client.wsTicket();
  if (res.ok) return `${url}?ticket=${encodeURIComponent(res.value.ticket)}`;
  return res.error.status === 401 ? "signedOut" : null;
}

/**
 * WebSocket URL for a lobby (same origin; the session cookie authenticates it,
 * or a ticket from withSocketTicket inside Discord, where the origin is
 * Discord's proxy and the same relative path reaches this server).
 */
export function lobbySocketUrl(lobbyId: string, loc: Pick<Location, "protocol" | "host"> = window.location): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${loc.host}/ws/lobby/${encodeURIComponent(lobbyId)}`;
}

/** The browser's IANA time zone (e.g. "America/Chicago"), or null. */
function localTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
